import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { dolDinhLucAuthor } from '@doltech/ui/lib/figma/Blog/dolDinhLucAuthor.data';
import { useTitleTemplateHeaderEditorPlugin } from '@doltech/ui/lib/figma/BlogEditor/BlogEditorPlugin.presets';
import {
  NormalizeTypeRule,
  withForceNormalizeTypes,
} from '@doltech/ui/lib/figma/BlogEditor/plugins/withForceNormalizeTypes';
import { withParagraphSoftBreak } from '@doltech/ui/lib/figma/BlogEditor/plugins/withParagraphSoftBreak';
import { trimEmptyParagraph } from '@doltech/ui/lib/figma/BlogEditor/shared-components/utils';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { DolEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditor';
import {
  InputAltComponent,
} from '@doltech/ui/lib/figma/DolEditor/elements/BlogEditor/MediaLogic/component/InputAltComponent';
import {
  InputCaptionComponent,
} from '@doltech/ui/lib/figma/DolEditor/elements/BlogEditor/MediaLogic/component/InputCaptionComponent';
import { ELEMENT_H1 } from '@doltech/ui/lib/slatePlugins';
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { getLengthEnumTextByKey } from '@doltech/utils/lib/enum/LengthEnum';
import { pipe } from '@doltech/utils/lib/pipe';
import { Space } from 'antd';
import { debounce, noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { useUpdate } from 'react-use';
import styled from 'styled-components';
import { Grid } from 'styled-css-grid';

import { Dot } from '../../../home-page/trending/TrendingComponent';
import { useStepStore } from '../../../step-header/model/base-step';
import { useAdminBlogStore } from '../../model/admin-create-blog.store';
import { useGetAdminEditorChains } from '@doltech/ui/lib/figma/BlogEditor/plugin-presets/useAdminEditorChains';

const StyleImage = styled.img`
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
`;

export const EditorPlaceholderWrapper = styled.div`
  > .dol-editor {
    min-height: 160px;
  }
  .title-template-placeholder {
    font-size: 29px;
    line-height: 36px;
  }
`;
const Wrapper = styled.div`
  background: ${colorsV2.white100};
  margin: 0 auto;
  > div {
    padding: 16px 24px 14px;
  }
`;

interface TitleTemplateComponentBaseProps {
  author: string;
  name: string;
  length?: string;
  thumbnail?: any;
  onUpdateCaption?: (caption: string) => void;
  onUpdateAlt?: (alt: string) => void;
  breadcrumbTitle?: string;
  children?: JSX.Element;

  isReadOnly?: boolean;
}

const forceTitleSubTitleNode: NormalizeTypeRule[] = [
  {
    path: [0],
    strictNode: {
      type: ELEMENT_H1,
      getDefaultValue: () => {
        return {
          type: ELEMENT_H1,
          id: nanoid(),
          placeholder: 'Please input title',
          children: [
            {
              text: '',
            },
          ],
        };
      },
    },
  },
  {
    path: [1],
    strictNode: {
      type: 'p',

      getDefaultValue: () => {
        return getEmptyParagraphNode('', { placeholder: 'Please input sub-title' });
      },
    },
  },
];

interface EditorWithPlaceHolderProps {
  onUpdateHeader?: (value: string) => void;
  initialValue: any;
  forceNormalizeTypes?: NormalizeTypeRule[];
  isReadOnly?: boolean;
}

const initEmptyValueFollowRule = (forceNormalizeTypes) => (arr) => {
  if (arr instanceof Array) {
    return arr.map((item, index) => {
      const foundRule = forceNormalizeTypes.find((rule) => rule.path[0] === index);
      if (foundRule && item?.type !== foundRule?.strictNode?.type) {
        return foundRule.strictNode.getDefaultValue();
      }
      return item;
    });
  }

  if (forceNormalizeTypes.length > 0) {
    return forceNormalizeTypes
      .filter((rule) => rule.path.length === 1)
      .map((rule) => {
        return rule.strictNode.getDefaultValue();
      });
  }
  return arr;
};

const removeOutOfBoundPath = (forceNormalizeTypes) => (arr) => {
  // TODO: disabled, need enable when it is daily learning
  // if (arr instanceof Array) {
  //   return arr.filter((item, index) => {
  //     return forceNormalizeTypes.find((rule) => rule.path[0] === index);
  //   });
  // }
  return arr;
};

export const EditorWithPlaceHolder = ({
  onUpdateHeader = noop,
  initialValue,
  forceNormalizeTypes = forceTitleSubTitleNode,
  isReadOnly = false,
}: EditorWithPlaceHolderProps) => {
  const [editorValue, setEditorValue] = React.useState(
    typeof initialValue === 'string' ? JSON.parse(initialValue) : initialValue,
  );
  const update = useUpdate();
  const { plugins, presets } = useTitleTemplateHeaderEditorPlugin({ isReadOnly });
  const debounceUpdate = React.useMemo(() => {
    return debounce((v) => {
      setEditorValue(v);
      update();
    }, 400);
  }, [update]);
  const withEditorChains = React.useMemo(() => {
    let resultEditorChains = [];
    const extraEditorChains = [
      withParagraphSoftBreak(),
      withForceNormalizeTypes({
        rules: forceNormalizeTypes,
      }),
    ];
    if (!isReadOnly) {
      resultEditorChains = extraEditorChains;
    }
    return resultEditorChains.filter(Boolean);
  }, [forceNormalizeTypes, isReadOnly]);
  const editorChains = useGetAdminEditorChains({ plugins, extraEditorChains: withEditorChains, presets });

  const titleEditorValue = React.useMemo(() => {
    if (isReadOnly) {
      return trimEmptyParagraph(editorValue);
    }
    return pipe(
      removeOutOfBoundPath(forceNormalizeTypes),
      initEmptyValueFollowRule(forceNormalizeTypes),
    )(editorValue);
  }, [editorValue, forceNormalizeTypes, isReadOnly]);

  return (
    <DolEditor
      readOnly={isReadOnly}
      onEditorChange={(v) => {
        onUpdateHeader(v as any);
        debounceUpdate(v);
      }}
      editorChains={editorChains}
      initialValue={titleEditorValue}
      plugins={plugins}

    />
  );
};

export const TitleTemplateComponentBase = ({
  author,
  name,
  length,
  thumbnail,
  children,
  onUpdateCaption = noop,
  onUpdateAlt = noop,
  isReadOnly,
  breadcrumbTitle = 'Breadcrumb',
}: TitleTemplateComponentBaseProps) => {
  return (
    <Wrapper className="title-template-wrapper">
      {thumbnail && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <StyleImage src={thumbnail?.url || ''} />
          <Grid rows={1} columns="auto max-content">
            <InputCaptionComponent
              caption={thumbnail?.caption || '{}'}
              onUpdateCaption={onUpdateCaption}
              isReadOnly={isReadOnly}
            />
            <InputAltComponent alt={thumbnail?.alt || ''} onUpdateAlt={onUpdateAlt} />
          </Grid>
        </Space>
      )}
      <Space direction="horizontal" size={14}>
        <Typography.Text variant="medium/14" color="gray40">
          {breadcrumbTitle}
        </Typography.Text>
        <IconChevronChevronRight />
        <Typography.Text variant="medium/14">{name}</Typography.Text>
      </Space>
      {children}
      <Grid gap="8px" rows={1} columns="repeat(3, max-content)">
        <Typography.Text variant="regular/16" color="gray40">
          {author === 'DOL' ? dolDinhLucAuthor.name : author}
        </Typography.Text>
        <Dot style={{ marginTop: 12 }} size={2} color={colorsV2.gray40} />
        {length && (
          <Typography.Text variant="regular/16" color="gray40">
            {getLengthEnumTextByKey(length)}
          </Typography.Text>
        )}
      </Grid>
    </Wrapper>
  );
};

interface TitleTemplateComponentProps {
  author: string;
  length: string;
  src: string;
  name: string;

  forceNormalizeTypes?: NormalizeTypeRule[];
}

export const TitleTemplateComponent = observer(
  ({
    author,
    length,
    src,
    name,
    forceNormalizeTypes = forceTitleSubTitleNode,
  }: TitleTemplateComponentProps) => {
    const adminBlogStore = useAdminBlogStore();
    const { header } = adminBlogStore;
    const stepStore = useStepStore();

    const updateHeaderDebounce = React.useMemo(() => {
      return debounce((value) => {
        adminBlogStore.updateHeader(JSON.stringify(value));
      }, 400);
    }, [adminBlogStore]);

    return (
      <TitleTemplateComponentBase
        author={author}
        length={length}
        name={name}
        thumbnail={{
          ...adminBlogStore.thumbnail,
          src,
        }}
        onUpdateCaption={(caption) => {
          adminBlogStore.updateThumbnail({ ...adminBlogStore.thumbnail, caption });
        }}
        onUpdateAlt={(alt) => {
          adminBlogStore.updateThumbnail({ ...adminBlogStore.thumbnail, alt });
        }}
      >
        <EditorPlaceholderWrapper>
          <EditorWithPlaceHolder
            forceNormalizeTypes={forceNormalizeTypes}
            onUpdateHeader={(value) => {
              updateHeaderDebounce(value);
              stepStore.getCurrentStep().valid(adminBlogStore.isContentValid);
            }}
            initialValue={header}
          />
        </EditorPlaceholderWrapper>
      </TitleTemplateComponentBase>
    );
  },
);
